import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { AnimatePresence, LayoutGroup, motion, useReducedMotion } from "framer-motion";
import Images from "../utils/Images";
import { REGIONS } from "../constants/navbarLinks";

const EASE = [0.22, 1, 0.36, 1];

function getLinkHref(link) {
  const raw = link.href || (link.id ? `#${link.id}` : "");
  if (raw.startsWith("#")) return `/${raw}`;
  return raw;
}

function getLinkLabel(link) {
  return link.label || link.title;
}

function useIsActive() {
  const { pathname, hash } = useLocation();
  const currentHash = hash || "#home";

  return (href) => {
    if (!href) return false;
    const hashHref = href.startsWith("/#") ? href.slice(1) : href.startsWith("#") ? href : null;
    if (hashHref) {
      if (pathname !== "/") return false;
      if (hashHref === "#home") {
        return !hash || hash === "#home" || hash === "#";
      }
      return currentHash === hashHref;
    }
    if (href === "/") {
      return pathname === "/" && (!hash || hash === "#home" || hash === "#");
    }
    return pathname === href || pathname.startsWith(`${href}/`);
  };
}

function useMotionTiming() {
  const reduce = useReducedMotion();
  return {
    reduce,
    enter: reduce ? { duration: 0 } : { duration: 0.28, ease: EASE },
    exit: reduce ? { duration: 0 } : { duration: 0.2, ease: EASE },
    spring: reduce
      ? { duration: 0 }
      : { type: "spring", stiffness: 380, damping: 32, mass: 0.7 },
  };
}

function Navbar({ links = [], services = [], resources = [] }) {
  const isActive = useIsActive();
  const { reduce, enter, exit, spring } = useMotionTiming();
  const [scrolled, setScrolled] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [regionOpen, setRegionOpen] = useState(false);
  const [region, setRegion] = useState(REGIONS[0].id);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobilePreviewId, setMobilePreviewId] = useState(null);
  const closeTimer = useRef(null);
  const headerRef = useRef(null);
  const solutionsId = useId();
  const resourcesId = useId();
  const regionId = useId();
  const activeRegion = REGIONS.find((item) => item.id === region) || REGIONS[0];

  const closeDesktopMenus = useCallback(() => {
    setSolutionsOpen(false);
    setResourcesOpen(false);
    setRegionOpen(false);
  }, []);

  const closeMenus = useCallback(() => {
    closeDesktopMenus();
    setMobileOpen(false);
    setMobilePreviewId(null);
  }, [closeDesktopMenus]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeMenus();
    };
    const onPointerDown = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        closeDesktopMenus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [closeDesktopMenus, closeMenus]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const openMenu = (menu) => {
    window.clearTimeout(closeTimer.current);
    setSolutionsOpen(menu === "solutions");
    setResourcesOpen(menu === "resources");
    setRegionOpen(menu === "region");
  };

  const scheduleCloseMenu = (menu) => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      if (menu === "solutions") setSolutionsOpen(false);
      if (menu === "resources") setResourcesOpen(false);
      if (menu === "region") setRegionOpen(false);
    }, 140);
  };

  const handleNavClick = () => closeMenus();

  const handleRegionSelect = (id) => {
    setRegion(id);
    setRegionOpen(false);
  };

  const serviceHrefs = services.map((service) => getLinkHref(service));
  const solutionsActive = serviceHrefs.some(isActive);
  const resourceHrefs = resources.map((item) => getLinkHref(item));
  const resourcesActive = resourceHrefs.some(isActive);
  const leadingLinks = links.filter((link) => ["home", "about"].includes(link.id));
  const faqsLink = links.find((link) => link.id === "faqs");
  const contactLink = links.find((link) => link.id === "contact");
  const contactHref = contactLink ? getLinkHref(contactLink) : "#contact";
  const contactActive = isActive(contactHref);
  const elevated = scrolled || solutionsOpen || resourcesOpen || regionOpen || mobileOpen;

  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-gold focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-navy"
      >
        Skip to main content
      </a>

      <motion.header
        ref={headerRef}
        className="fixed inset-x-0 top-0 z-50 w-full"
        initial={reduce ? false : { y: -18, opacity: 0 }}
        animate={{
          y: 0,
          opacity: 1,
          backgroundColor: elevated ? "rgba(255,255,255,1)" : "rgba(255,255,255,0)",
          boxShadow: elevated
            ? "0 12px 40px rgba(12,31,27,0.08)"
            : "0 0 0 rgba(12,31,27,0)",
        }}
        transition={enter}
      >
        <motion.div
          className="h-[3px] w-full origin-left bg-gradient-to-r from-navy via-royal to-gold"
          initial={reduce ? false : { scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ ...enter, duration: reduce ? 0 : 0.55 }}
        />

        <nav className="mx-auto flex h-[4.5rem] max-w-8xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8" aria-label="Primary">
          <motion.a
            href="/#home"
            className="flex shrink-0 items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal focus-visible:ring-offset-2"
            onClick={handleNavClick}
            whileHover={reduce ? undefined : { scale: 1.02 }}
            whileTap={reduce ? undefined : { scale: 0.98 }}
            transition={spring}
          >
            <img src={Images.logo} alt="SimFinance" className="h-10 w-auto sm:h-11" />
          </motion.a>

          <LayoutGroup id="nav-active">
            <div className="hidden items-center gap-1 lg:flex">
              {leadingLinks.map((link) => (
                <NavAnchor
                  key={link.id}
                  href={getLinkHref(link)}
                  onClick={handleNavClick}
                  isActive={isActive(getLinkHref(link))}
                  layoutTransition={spring}
                >
                  {getLinkLabel(link)}
                </NavAnchor>
              ))}

              <div
                onMouseEnter={() => openMenu("solutions")}
                onMouseLeave={() => scheduleCloseMenu("solutions")}
              >
                <motion.button
                  type="button"
                  className={`group relative inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-lg px-3.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal ${
                    solutionsOpen || solutionsActive
                      ? "bg-navy/[0.04] text-royal"
                      : "text-navy/80 hover:bg-navy/[0.04] hover:text-navy"
                  }`}
                  aria-expanded={solutionsOpen}
                  aria-controls={solutionsId}
                  aria-haspopup="true"
                  aria-current={solutionsActive ? "page" : undefined}
                  onClick={() => openMenu(solutionsOpen ? "" : "solutions")}
                  whileHover={reduce ? undefined : { y: -1 }}
                  whileTap={reduce ? undefined : { scale: 0.98 }}
                >
                  Solutions
                  <ChevronIcon open={solutionsOpen} />
                  {solutionsActive ? (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute inset-x-3.5 -bottom-0.5 h-0.5 rounded-full bg-gold"
                      transition={spring}
                    />
                  ) : (
                    <span className="absolute inset-x-3.5 -bottom-0.5 h-0.5 origin-left scale-x-0 rounded-full bg-gold transition duration-200 group-hover:scale-x-100" />
                  )}
                </motion.button>
              </div>

              <div
                className="relative"
                onMouseEnter={() => openMenu("resources")}
                onMouseLeave={() => scheduleCloseMenu("resources")}
              >
                <motion.button
                  type="button"
                  className={`group relative inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-lg px-3.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal ${
                    resourcesOpen || resourcesActive
                      ? "bg-navy/[0.04] text-royal"
                      : "text-navy/80 hover:bg-navy/[0.04] hover:text-navy"
                  }`}
                  aria-expanded={resourcesOpen}
                  aria-controls={resourcesId}
                  aria-haspopup="true"
                  aria-current={resourcesActive ? "page" : undefined}
                  onClick={() => openMenu(resourcesOpen ? "" : "resources")}
                  whileHover={reduce ? undefined : { y: -1 }}
                  whileTap={reduce ? undefined : { scale: 0.98 }}
                >
                  Resources
                  <ChevronIcon open={resourcesOpen} />
                  {resourcesActive ? (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute inset-x-3.5 -bottom-0.5 h-0.5 rounded-full bg-gold"
                      transition={spring}
                    />
                  ) : (
                    <span className="absolute inset-x-3.5 -bottom-0.5 h-0.5 origin-left scale-x-0 rounded-full bg-gold transition duration-200 group-hover:scale-x-100" />
                  )}
                </motion.button>

                <AnimatePresence>
                  {resourcesOpen ? (
                    <ResourcesMenu
                      id={resourcesId}
                      items={resources}
                      onNavigate={handleNavClick}
                      isActive={isActive}
                      timing={{ reduce, enter, exit }}
                    />
                  ) : null}
                </AnimatePresence>
              </div>

              {faqsLink ? (
                <NavAnchor
                  href={getLinkHref(faqsLink)}
                  onClick={handleNavClick}
                  isActive={isActive(getLinkHref(faqsLink))}
                  layoutTransition={spring}
                >
                  {getLinkLabel(faqsLink)}
                </NavAnchor>
              ) : null}
            </div>
          </LayoutGroup>

          <div className="hidden items-center gap-3 lg:flex">
            <div className="relative" onMouseLeave={() => scheduleCloseMenu("region")}>
              <motion.button
                type="button"
                className={`inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-full border px-3.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal focus-visible:ring-offset-2 ${
                  regionOpen
                    ? "border-navy/20 bg-navy/[0.04] text-navy"
                    : "border-navy/15 text-navy/80 hover:border-navy/25 hover:text-navy"
                }`}
                aria-expanded={regionOpen}
                aria-controls={regionId}
                aria-haspopup="listbox"
                aria-label={`Market: ${activeRegion.name}. Content will change by market later.`}
                onClick={() => openMenu(regionOpen ? "" : "region")}
                whileHover={reduce ? undefined : { y: -1 }}
                whileTap={reduce ? undefined : { scale: 0.98 }}
              >
                <RegionMark id={activeRegion.id} />
                {activeRegion.name}
                <ChevronIcon open={regionOpen} />
              </motion.button>

              <AnimatePresence>
                {regionOpen ? (
                  <RegionMenu
                    id={regionId}
                    selected={region}
                    onSelect={handleRegionSelect}
                    timing={{ reduce, enter, exit }}
                  />
                ) : null}
              </AnimatePresence>
            </div>

            <motion.a
              href={contactHref}
              aria-current={contactActive ? "page" : undefined}
              className={`inline-flex min-h-11 cursor-pointer items-center rounded-full px-5 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal focus-visible:ring-offset-2 ${
                contactActive
                  ? "bg-navy text-white shadow-[0_8px_20px_rgba(12,31,27,0.18)]"
                  : "bg-gold text-navy"
              }`}
              onClick={handleNavClick}
              whileHover={reduce ? undefined : { scale: 1.04, y: -1 }}
              whileTap={reduce ? undefined : { scale: 0.97 }}
              transition={spring}
            >
              {contactLink ? getLinkLabel(contactLink) : "Contact Us"}
            </motion.a>
          </div>

          <motion.button
            type="button"
            className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-navy hover:bg-navy/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal lg:hidden"
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileOpen((open) => !open)}
            whileTap={reduce ? undefined : { scale: 0.92 }}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={mobileOpen ? "close" : "menu"}
                initial={reduce ? false : { rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={reduce ? { opacity: 1 } : { rotate: 90, opacity: 0 }}
                transition={exit}
                className="flex"
              >
                {mobileOpen ? <CloseIcon /> : <MenuIcon />}
              </motion.span>
            </AnimatePresence>
          </motion.button>
        </nav>

        <div className={`h-px w-full transition-colors duration-300 ${elevated ? "bg-navy/10" : "bg-transparent"}`} />

        <div
          id={solutionsId}
          className="hidden lg:block"
          onMouseEnter={() => openMenu("solutions")}
          onMouseLeave={() => scheduleCloseMenu("solutions")}
        >
          <AnimatePresence>
            {solutionsOpen ? (
              <MegaMenu
                onNavigate={handleNavClick}
                services={services}
                isActive={isActive}
                timing={{ reduce, enter, exit }}
              />
            ) : null}
          </AnimatePresence>
        </div>

        <AnimatePresence initial={false}>
          {mobileOpen ? (
            <motion.div
              id="mobile-nav"
              className="overflow-hidden border-t border-navy/10 bg-white lg:hidden"
              initial={reduce ? { opacity: 1 } : { height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={reduce ? { opacity: 1 } : { height: 0, opacity: 0 }}
              transition={enter}
            >
              <div className="flex max-h-[calc(100dvh-5.25rem)] flex-col">
                <div className="flex-1 overflow-y-auto px-5 pt-7">
                  <nav aria-label="Mobile">
                    {leadingLinks.map((link, index) => (
                      <motion.div
                        key={link.id}
                        initial={reduce ? false : { opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ ...enter, delay: reduce ? 0 : index * 0.05 }}
                      >
                        <MobileLink
                          href={getLinkHref(link)}
                          onClick={handleNavClick}
                          isActive={isActive(getLinkHref(link))}
                        >
                          {getLinkLabel(link)}
                        </MobileLink>
                      </motion.div>
                    ))}
                  </nav>

                  <motion.div
                    className="mt-8 border-t border-navy/10 pt-7"
                    initial={reduce ? false : { opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ ...enter, delay: reduce ? 0 : 0.12 }}
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
                      Solutions
                    </p>
                    <ul className="mt-4">
                      {services.map((service) => {
                        const href = getLinkHref(service);
                        const selected = mobilePreviewId === service.id;
                        const current = isActive(href);

                        return (
                          <li key={service.id} className="border-b border-navy/10 last:border-b-0">
                            <button
                              type="button"
                              className="group relative flex w-full min-h-14 cursor-pointer items-start justify-between gap-4 py-4 pl-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal"
                              aria-expanded={selected}
                              aria-current={current ? "page" : undefined}
                              onClick={() =>
                                setMobilePreviewId((id) => (id === service.id ? null : service.id))
                              }
                            >
                              {selected || current ? (
                                <span className="absolute bottom-4 left-0 top-4 w-[2px] bg-gold" />
                              ) : null}
                              <span>
                                <span className="block text-[11px] font-semibold tracking-[0.22em] text-navy/30">
                                  {service.index}
                                </span>
                                <span
                                  className={`mt-1 block text-xl font-semibold tracking-tight ${
                                    selected || current ? "text-navy" : "text-navy/45"
                                  }`}
                                >
                                  {service.menuLabel || service.name}
                                </span>
                                <span className="mt-1 block text-sm text-navy/50">{service.description}</span>
                              </span>
                              <ChevronIcon open={selected} />
                            </button>

                            <AnimatePresence initial={false}>
                              {selected ? (
                                <motion.div
                                  className="overflow-hidden"
                                  initial={reduce ? false : { height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={reduce ? { opacity: 1 } : { height: 0, opacity: 0 }}
                                  transition={enter}
                                >
                                  <div
                                    className="mb-4 overflow-hidden bg-navy px-4 py-5 text-white"
                                    style={{
                                      backgroundImage:
                                        "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
                                      backgroundSize: "36px 36px",
                                    }}
                                  >
                                    <div className="mb-4 flex items-center justify-between gap-4">
                                      <img
                                        src={service.logo}
                                        alt=""
                                        className="h-12 w-auto max-w-[14rem] object-contain object-left"
                                      />
                                      <ProductVisual type={service.visual} reduce={reduce} />
                                    </div>
                                    <p className="text-sm leading-relaxed text-white/70">{service.detail}</p>
                                    <dl className="mt-4 divide-y divide-white/10 border-y border-white/10">
                                      {service.spec?.map((row) => (
                                        <div key={row.label} className="grid grid-cols-[4.5rem_1fr] gap-3 py-2.5">
                                          <dt className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/35">
                                            {row.label}
                                          </dt>
                                          <dd className="text-sm text-white/90">{row.value}</dd>
                                        </div>
                                      ))}
                                    </dl>
                                    <a
                                      href={href}
                                      onClick={handleNavClick}
                                      className="mt-5 inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                                    >
                                      Continue to {service.name}
                                      <ArrowIcon />
                                    </a>
                                  </div>
                                </motion.div>
                              ) : null}
                            </AnimatePresence>
                          </li>
                        );
                      })}
                    </ul>
                  </motion.div>

                  {resources.length ? (
                    <motion.div
                      className="mt-8 border-t border-navy/10 pt-7"
                      initial={reduce ? false : { opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ ...enter, delay: reduce ? 0 : 0.16 }}
                    >
                      <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
                        Resources
                      </p>
                      <ul className="mt-3">
                        {resources.map((item) => {
                          const href = getLinkHref(item);
                          const current = isActive(href);
                          const className =
                            "block min-h-14 cursor-pointer py-3 pl-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal";
                          const content = (
                            <>
                              <span
                                className={`block text-xl font-semibold tracking-tight ${
                                  current ? "text-navy" : "text-navy/45"
                                }`}
                              >
                                {item.name}
                              </span>
                              <span className="mt-1 block text-sm text-navy/50">{item.description}</span>
                            </>
                          );

                          return (
                            <li key={item.id} className="border-b border-navy/10 last:border-b-0">
                              {href.includes("#") ? (
                                <a href={href} onClick={handleNavClick} aria-current={current ? "page" : undefined} className={className}>
                                  {content}
                                </a>
                              ) : (
                                <Link to={href} onClick={handleNavClick} aria-current={current ? "page" : undefined} className={className}>
                                  {content}
                                </Link>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </motion.div>
                  ) : null}

                  {faqsLink ? (
                    <motion.div
                      className="mt-2"
                      initial={reduce ? false : { opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ ...enter, delay: reduce ? 0 : 0.18 }}
                    >
                      <MobileLink
                        href={getLinkHref(faqsLink)}
                        onClick={handleNavClick}
                        isActive={isActive(getLinkHref(faqsLink))}
                      >
                        {getLinkLabel(faqsLink)}
                      </MobileLink>
                    </motion.div>
                  ) : null}
                </div>

                <div className="border-t border-navy/10 bg-white px-5 py-4">
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
                    Market
                  </p>
                  <div className="mb-3 grid grid-cols-2 gap-2" role="listbox" aria-label="Market">
                    {REGIONS.map((item) => {
                      const selected = item.id === region;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          className={`inline-flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-full border text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal ${
                            selected
                              ? "border-navy bg-navy text-white"
                              : "border-navy/15 text-navy/70 hover:border-navy/30 hover:text-navy"
                          }`}
                          onClick={() => handleRegionSelect(item.id)}
                        >
                          <RegionMark id={item.id} />
                          {item.name}
                        </button>
                      );
                    })}
                  </div>
                  <motion.a
                    href={contactHref}
                    aria-current={contactActive ? "page" : undefined}
                    className={`inline-flex min-h-12 w-full cursor-pointer items-center justify-center rounded-full px-5 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal ${
                      contactActive ? "bg-navy text-white" : "bg-gold text-navy"
                    }`}
                    onClick={handleNavClick}
                    whileTap={reduce ? undefined : { scale: 0.98 }}
                  >
                    {contactLink ? getLinkLabel(contactLink) : "Contact Us"}
                  </motion.a>
                </div>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.header>
    </>
  );
}

function MegaMenu({ onNavigate, services, isActive, timing }) {
  const { reduce, enter, exit } = timing;
  const routed = services.find((service) => isActive(getLinkHref(service)));
  const [previewId, setPreviewId] = useState(routed?.id || services[0]?.id);
  const preview = services.find((service) => service.id === previewId) || services[0];

  if (!preview) return null;

  return (
    <motion.div
      className="overflow-hidden border-t border-navy/10 bg-white"
      role="region"
      aria-label="Solutions"
      initial={reduce ? { opacity: 1 } : { height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={reduce ? { opacity: 1, height: 0 } : { height: 0, opacity: 0 }}
      transition={exit}
    >
      <div className="mx-auto grid max-w-8xl lg:grid-cols-[minmax(0,1fr)_minmax(22rem,40rem)]">
        <div className="px-4 py-10 sm:px-6 lg:px-8 lg:py-12">
          <motion.p
            className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40"
            initial={reduce ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={enter}
          >
            Solutions
          </motion.p>

          <LayoutGroup id="mega-preview">
            <ul className="mt-8 space-y-1">
              {services.map((service, index) => {
                const href = getLinkHref(service);
                const selected = service.id === preview.id;
                const current = isActive(href);

                return (
                  <motion.li
                    key={service.id}
                    initial={reduce ? false : { opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ ...enter, delay: reduce ? 0 : 0.04 * index }}
                  >
                    <a
                      href={href}
                      aria-current={current ? "page" : undefined}
                      className="group relative block cursor-pointer py-4 pl-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal"
                      onMouseEnter={() => setPreviewId(service.id)}
                      onFocus={() => setPreviewId(service.id)}
                      onClick={onNavigate}
                    >
                      {selected ? (
                        <motion.span
                          layoutId="mega-indicator"
                          className="absolute bottom-4 left-0 top-4 w-[2px] bg-gold"
                          transition={{ type: "spring", stiffness: 380, damping: 34 }}
                        />
                      ) : null}
                      <span
                        className={`block text-[1.65rem] font-semibold leading-tight tracking-tight transition-colors ${
                          selected ? "text-navy" : "text-navy/35 group-hover:text-navy/70"
                        }`}
                      >
                        {service.menuLabel || service.name}
                      </span>
                      <span
                        className={`mt-2 block max-w-md text-sm leading-relaxed transition-colors ${
                          selected ? "text-navy/60" : "text-navy/30"
                        }`}
                      >
                        {service.description}
                      </span>
                    </a>
                  </motion.li>
                );
              })}
            </ul>
          </LayoutGroup>

          <div className="mt-10 flex items-center gap-6 border-t border-navy/10 pt-6 text-sm">
            <a
              href="/#contact"
              onClick={onNavigate}
              className="font-semibold text-navy hover:text-royal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal"
            >
              Speak with an advisor
            </a>
            <span className="text-navy/20">/</span>
            <Link
              to="/about"
              onClick={onNavigate}
              className="text-navy/50 hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal"
            >
              About SimFinance
            </Link>
          </div>
        </div>

        <div
          className="relative min-h-[24rem] overflow-hidden bg-navy text-white"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        >
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/80 to-transparent" />
          <div className="pointer-events-none absolute -right-20 top-8 h-64 w-64 rounded-full bg-royal/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 left-0 h-56 w-56 rounded-full bg-gold/10 blur-3xl" />

          <AnimatePresence mode="wait">
            <motion.div
              key={preview.id}
              className="relative flex h-full min-h-[24rem] flex-col justify-between px-8 py-10 lg:px-12 lg:py-12"
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={reduce ? { opacity: 1 } : { opacity: 0 }}
              transition={enter}
            >
              <p
                aria-hidden="true"
                className="pointer-events-none absolute -right-4 top-16 select-none text-[5.5rem] font-semibold leading-none tracking-tight text-white/[0.04]"
              >
                {preview.name}
              </p>

              <div className="relative flex items-start justify-between gap-6">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-gold/90">
                    {preview.index} / 03
                  </p>
                  <img
                    src={preview.logo}
                    alt=""
                    className="mt-6 h-20 w-auto max-w-[20rem] object-contain object-left sm:h-24"
                  />
                </div>
                <ProductVisual type={preview.visual} reduce={reduce} />
              </div>

              <div className="relative mt-10 max-w-md">
                <p className="text-[15px] leading-relaxed text-white/72">{preview.detail}</p>

                <dl className="mt-8 divide-y divide-white/10 border-y border-white/10">
                  {preview.spec?.map((row) => (
                    <div key={row.label} className="grid grid-cols-[5.5rem_1fr] gap-4 py-3">
                      <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/35">
                        {row.label}
                      </dt>
                      <dd className="text-sm text-white/90">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <a
                href={getLinkHref(preview)}
                onClick={onNavigate}
                className="group relative mt-10 inline-flex items-center gap-3 self-start pb-1 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
              >
                Continue to {preview.name}
                <ArrowIcon className="text-gold transition group-hover:translate-x-0.5" />
                <span className="absolute inset-x-0 bottom-0 h-px origin-left scale-x-[0.28] bg-gold transition duration-300 group-hover:scale-x-100" />
              </a>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}

function ProductVisual({ type, reduce }) {
  const draw = reduce
    ? { duration: 0 }
    : { duration: 0.7, ease: EASE };

  if (type === "collect") {
    return (
      <svg viewBox="0 0 120 88" className="h-20 w-28 shrink-0" aria-hidden="true">
        {[18, 36, 54, 72].map((y, i) => (
          <motion.rect
            key={y}
            x="8"
            y={y}
            height="6"
            rx="3"
            fill={i === 3 ? "#E5B80B" : i === 2 ? "#EF4444" : "rgba(255,255,255,0.22)"}
            initial={reduce ? false : { width: 0 }}
            animate={{ width: 28 + i * 22 }}
            transition={{ ...draw, delay: reduce ? 0 : 0.08 * i }}
          />
        ))}
      </svg>
    );
  }

  if (type === "verify") {
    return (
      <svg viewBox="0 0 120 88" className="h-20 w-28 shrink-0" fill="none" aria-hidden="true">
        <motion.path
          d="M60 10 L98 24 V48 C98 68 82 80 60 84 C38 80 22 68 22 48 V24 Z"
          stroke="rgba(12,31,27,0.95)"
          strokeWidth="2"
          initial={reduce ? false : { pathLength: 0, opacity: 0.4 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={draw}
        />
        <motion.path
          d="M44 46 L56 58 L78 34"
          stroke="#E5B80B"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={reduce ? false : { pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ ...draw, delay: reduce ? 0 : 0.25 }}
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 120 88" className="h-20 w-28 shrink-0" aria-hidden="true">
      {[
        { x: 18, h: 28 },
        { x: 48, h: 44 },
        { x: 78, h: 62 },
      ].map((bar, i) => (
        <motion.rect
          key={bar.x}
          x={bar.x}
          width="18"
          rx="3"
          fill={i === 2 ? "#E5B80B" : "rgba(12,31,27,0.85)"}
          initial={reduce ? false : { height: 0, y: 78 }}
          animate={{ height: bar.h, y: 78 - bar.h }}
          transition={{ ...draw, delay: reduce ? 0 : 0.1 * i }}
        />
      ))}
      <motion.path
        d="M16 22 L86 22 L74 12 M86 22 L74 32"
        fill="none"
        stroke="rgba(255,255,255,0.55)"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={reduce ? false : { pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ ...draw, delay: reduce ? 0 : 0.35 }}
      />
    </svg>
  );
}

function ResourcesMenu({ id, items, onNavigate, isActive, timing }) {
  const { reduce, enter, exit } = timing;

  return (
    <motion.div
      id={id}
      className="absolute left-1/2 top-full z-50 w-[19.5rem] -translate-x-1/2 pt-2"
      initial={reduce ? { opacity: 1 } : { opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduce ? { opacity: 1 } : { opacity: 0, y: -4 }}
      transition={exit}
    >
      <div className="border border-navy/10 bg-white shadow-[0_18px_40px_rgba(12,31,27,0.1)]" role="menu" aria-label="Resources">
        <p className="px-4 pt-4 text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
          Learn more
        </p>
        <ul className="p-2 pb-3">
          {items.map((item, index) => {
            const href = getLinkHref(item);
            const current = isActive(href);
            const className = `group block cursor-pointer px-3 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal ${
              current ? "bg-navy/[0.04]" : "hover:bg-navy/[0.04]"
            }`;
            const content = (
              <>
                <span className="flex items-center justify-between gap-3">
                  <span className={`text-sm font-semibold ${current ? "text-royal" : "text-navy"}`}>
                    {item.name}
                  </span>
                  <ArrowIcon className="text-navy/25 transition group-hover:translate-x-0.5 group-hover:text-gold" />
                </span>
                <span className="mt-1 block text-xs leading-relaxed text-navy/50">{item.description}</span>
              </>
            );

            return (
              <motion.li
                key={item.id}
                role="none"
                initial={reduce ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...enter, delay: reduce ? 0 : 0.04 * index }}
              >
                {href.includes("#") ? (
                  <a href={href} role="menuitem" aria-current={current ? "page" : undefined} className={className} onClick={onNavigate}>
                    {content}
                  </a>
                ) : (
                  <Link to={href} role="menuitem" aria-current={current ? "page" : undefined} className={className} onClick={onNavigate}>
                    {content}
                  </Link>
                )}
              </motion.li>
            );
          })}
        </ul>
      </div>
    </motion.div>
  );
}

function RegionMenu({ id, selected, onSelect, timing }) {
  const { reduce, enter, exit } = timing;

  return (
    <motion.div
      id={id}
      className="absolute right-0 top-full z-50 w-56 pt-2"
      initial={reduce ? { opacity: 1 } : { opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduce ? { opacity: 1 } : { opacity: 0, y: -4 }}
      transition={exit}
    >
      <div className="border border-navy/10 bg-white p-2 shadow-[0_18px_40px_rgba(12,31,27,0.1)]" role="listbox" aria-label="Market">
        {REGIONS.map((item, index) => {
          const current = item.id === selected;
          return (
            <motion.button
              key={item.id}
              type="button"
              role="option"
              aria-selected={current}
              className={`flex w-full min-h-12 cursor-pointer items-center gap-3 px-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal ${
                current ? "bg-navy/[0.04]" : "hover:bg-navy/[0.04]"
              }`}
              onClick={() => onSelect(item.id)}
              initial={reduce ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...enter, delay: reduce ? 0 : 0.04 * index }}
            >
              <RegionMark id={item.id} />
              <span className="min-w-0 flex-1">
                <span className={`block text-sm font-semibold ${current ? "text-navy" : "text-navy/70"}`}>
                  {item.name}
                </span>
                <span className="block text-[11px] text-navy/40">{item.description}</span>
              </span>
              {current ? <CheckIcon /> : null}
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}

function RegionMark({ id }) {
  if (id === "global") {
    return (
      <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0 text-royal" fill="none" aria-hidden="true">
        <circle cx="10" cy="10" r="7.25" stroke="currentColor" strokeWidth="1.6" />
        <path d="M10 2.75C12.2 5.1 13.1 7.55 13.1 10S12.2 14.9 10 17.25C7.8 14.9 6.9 12.45 6.9 10S7.8 5.1 10 2.75Z" stroke="currentColor" strokeWidth="1.4" />
        <path d="M3.2 10H16.8M4.4 6.6H15.6M4.4 13.4H15.6" stroke="currentColor" strokeWidth="1.3" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 20 14" className="h-3.5 w-5 shrink-0 overflow-hidden" aria-hidden="true">
      <rect width="20" height="14" fill="#CE1126" />
      <rect y="4.67" width="20" height="4.66" fill="#FCD116" />
      <rect y="9.33" width="20" height="4.67" fill="#006B3F" />
      <path
        d="M10 4.7L10.74 6.96H13.12L11.19 8.36L11.93 10.62L10 9.22L8.07 10.62L8.81 8.36L6.88 6.96H9.26L10 4.7Z"
        fill="#000"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0 text-gold" fill="none" aria-hidden="true">
      <path d="M3.5 8.2L6.4 11.1L12.5 4.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function NavAnchor({ href, onClick, children, isActive, layoutTransition }) {
  const className = `group relative inline-flex min-h-11 cursor-pointer items-center px-3.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal ${
    isActive ? "font-semibold text-royal" : "font-medium text-navy/80 hover:text-navy"
  }`;

  const content = (
    <>
      {children}
      {isActive ? (
        <motion.span
          layoutId="nav-underline"
          className="absolute inset-x-3.5 -bottom-0.5 h-0.5 rounded-full bg-gold"
          transition={layoutTransition}
        />
      ) : (
        <span className="absolute inset-x-3.5 -bottom-0.5 h-0.5 origin-left scale-x-0 rounded-full bg-gold transition duration-200 group-hover:scale-x-100" />
      )}
    </>
  );

  if (href.includes("#")) {
    return (
      <a href={href} onClick={onClick} aria-current={isActive ? "page" : undefined} className={className}>
        {content}
      </a>
    );
  }

  return (
    <Link to={href} onClick={onClick} aria-current={isActive ? "page" : undefined} className={className}>
      {content}
    </Link>
  );
}

function MobileLink({ href, onClick, children, isActive }) {
  const className =
    "relative block min-h-14 cursor-pointer py-3 pl-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal";
  const content = (
    <>
      {isActive ? <span className="absolute bottom-3 left-0 top-3 w-[2px] bg-gold" /> : null}
      <span
        className={`block text-[1.65rem] font-semibold leading-none tracking-tight ${
          isActive ? "text-navy" : "text-navy/40"
        }`}
      >
        {children}
      </span>
    </>
  );

  if (href.includes("#")) {
    return (
      <a href={href} onClick={onClick} aria-current={isActive ? "page" : undefined} className={className}>
        {content}
      </a>
    );
  }

  return (
    <Link to={href} onClick={onClick} aria-current={isActive ? "page" : undefined} className={className}>
      {content}
    </Link>
  );
}

function ChevronIcon({ open }) {
  const reduce = useReducedMotion();
  return (
    <motion.svg
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className="h-4 w-4"
      animate={{ rotate: open ? 180 : 0 }}
      transition={reduce ? { duration: 0 } : { duration: 0.22, ease: EASE }}
    >
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </motion.svg>
  );
}

function MenuIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-6 w-6">
      <path d="M4 7H20M4 12H20M4 17H20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-6 w-6">
      <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function ArrowIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className={`h-3.5 w-3.5 ${className}`}>
      <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default Navbar;
